/**
 * Secura Web : notifications du navigateur (bandeau + son) quand un message ou une alerte arrive
 * alors que l'onglet est en arrière-plan, ou qu'un autre projet est ouvert.
 * Le serveur envoie déjà « notification:push » à chaque notification (messages, mentions, tâches, congés…).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSocketOptional } from '../contexts/SocketContext';

/** Petit « ding » généré (pas de fichier son à charger). */
function ding() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.18);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.36);
    osc.onended = () => ctx.close();
  } catch {
    /* son facultatif */
  }
}

const pathFor = (data = {}) => {
  const taskId = Number(data.taskId) || null;
  if (taskId && String(data.type || '').startsWith('task')) return `/tasks/${taskId}`;
  const projetId = Number(data.projetId) || null;
  if (projetId) return `/projets/${projetId}?tab=conversation`;
  return '/projets';
};

export default function useWebNotifications() {
  const { socket } = useSocketOptional();
  const navigate = useNavigate();
  const location = useLocation();
  const [permission, setPermission] = useState(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
  );
  const locRef = useRef(location);
  locRef.current = location;

  const request = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    const p = await Notification.requestPermission();
    setPermission(p);
    if (p === 'granted') toast.success('Notifications activées');
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    const onPush = (payload = {}) => {
      const data = payload.data || {};
      const target = pathFor(data);
      const openHere = locRef.current.pathname === target.split('?')[0];
      const hidden = document.visibilityState !== 'visible';
      if (!hidden && openHere) return; // déjà sous les yeux
      ding();
      if (hidden && permission === 'granted') {
        const n = new Notification(payload.title || 'Secura', {
          body: payload.body || '',
          icon: '/icon-192x192.png',
          tag: data.projetId ? `projet-${data.projetId}` : undefined,
          renotify: true,
        });
        n.onclick = () => {
          window.focus();
          navigate(target);
          n.close();
        };
      } else if (!hidden) {
        toast(`${payload.title || 'Secura'} — ${payload.body || ''}`.slice(0, 140), { duration: 5000 });
      }
    };
    socket.on('notification:push', onPush);
    return () => socket.off('notification:push', onPush);
  }, [socket, permission, navigate]);

  return { permission, request };
}
